import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useContext,
} from "react";
import {
  Button,
  TextField,
  Checkbox,
  Grid,
  Skeleton,
  IconButton,
  Collapse,
  Alert,
  Box,
} from "@mui/material/";

// ICONS OPEN
import {
  Delete,
  Folder,
  FolderOpen,
  InsertDriveFile,
  CloudUpload,
  Info,
} from "@mui/icons-material";
import { encodeS3Path, getS3Url } from "../../../../Components/helpers/Helpers";
import {
  DocsContext,
  PermissionContext,
} from "../../../../Components/Context/Context";
import { CustomIconsPlusText } from "../../../../Components/Common/Design/CustomIconsPlusText";
import api from "../../../../api/axios";

const ArchivosMostrar = ({
  caseId,
  userLogged,
  setDocsChangedFlag,
  docsChangedFlag,
  selectedDocs,
  setSelectedDocs,
  dataCase,
}) => {
  const Swal = require("sweetalert2");

  const { can } = useContext(PermissionContext);

  const { docsInfo, docsInfoTransfered, docFlagTransfered, docsFlag } =
    useContext(DocsContext);
  // Estado para controlar qué carpetas están abiertas
  const [openFolders, setOpenFolders] = useState({});
  const [docFlag, setDocFlag] = useState(false); // Indica si los documentos ya están listos para mostrar
  // Cambia el estado de una carpeta (abre/cierra)
  const toggleFolder = (folderKey) => {
    setOpenFolders((prev) => ({
      ...prev,
      [folderKey]: !prev[folderKey],
    }));
  };

  // Maneja el cambio de nombre de un documento
  const handleNewDocumentName = (fileNewTitle, fileOldName, fullpath) => {
    const getInputId = document.getElementById(fileNewTitle);

    if (getInputId.value !== "") {
      Swal.fire({
        title: "¿Desea guardar el nombre del documento?",
        icon: "warning",
        showDenyButton: true,
        confirmButtonColor: "#1ab394",
        cancelButtonColor: "#d33",
        reverseButtons: true,
        confirmButtonText: "Sí",
        cancelButtonText: "Cancelar",
      }).then(async (result) => {
        if (result.isConfirmed) {
          //  Mostrar alerta de carga
          Swal.fire({
            title: "Guardando...",
            text: "Por favor espera un momento",
            allowOutsideClick: false,
            allowEscapeKey: false,
            didOpen: () => {
              Swal.showLoading();
            },
          });

          try {
            const formData = new FormData();
            formData.append("case_id", caseId);
            formData.append("doc_title", getInputId.value);
            formData.append("doc_file", fullpath);
            formData.append("doc_status", 1);
            formData.append("user_id", userLogged?.id);

            const { data } = await api.post(
              `${process.env.REACT_APP_API_URL}/docs/newdoctitle`,
              formData,
            );

            Swal.close(); //  Cierra la alerta de carga

            if (data?.error?.errorInfo?.length > 0) {
              Swal.fire({
                title: "Error",
                text: "¡Ha ocurrido un error al subir el nombre del documento!",
                icon: "error",
                confirmButtonColor: "#1ab394",
                confirmButtonText: "Continuar",
              });
            } else {
              Swal.fire({
                title: "¡Guardado correctamente!",
                icon: "success",
                confirmButtonColor: "#1ab394",
                confirmButtonText: "Continuar",
              });
              setDocsChangedFlag(!docsChangedFlag);
            }
          } catch (error) {
            Swal.close(); // Cierra la carga si hay error inesperado
            Swal.fire({
              title: "Error",
              text: "No se pudo completar la solicitud.",
              icon: "error",
              confirmButtonColor: "#1ab394",
              confirmButtonText: "Continuar",
            });
            console.error(error);
          }
        }
      });
    } else {
      Swal.fire({
        title: "No se ha detectado el nombre del archivo",
        icon: "warning",
        confirmButtonColor: "#1ab394",
        confirmButtonText: "Continuar",
      });
    }
  };

  // Elimina un archivo del servidor
  const handleDelete = (ruta) => {
    const formData = new FormData();

    // console.log("Respuesta:", ruta);
    // console.log("case_id:", caseId);
    formData.append("case_id", caseId);
    formData.append("full_path", ruta?.path);
    formData.append("doc_id", ruta?.doc_id ? ruta?.doc_id : 0);

    try {
      Swal.fire({
        title: "¿Seguro que desea eliminar el archivo?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#1ab394",
        cancelButtonColor: "#d33",
        reverseButtons: true,
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      }).then(async (response) => {
        if (response.isConfirmed) {
          Swal.fire({
            title: "Eliminando archivos...",
            html: '<div class="swal-loader"></div>',
            allowOutsideClick: false,
            showConfirmButton: false,
            willOpen: () => {
              Swal.showLoading();
            },
          });
          try {
            await api.post(
              `${process.env.REACT_APP_API_URL}/s3/destroy`,
              formData,
            );
            Swal.close();

            Swal.fire({
              title: "¡Eliminado!",
              icon: "success",
              text: "La documentación se ha eliminado exitosamente",
              confirmButtonColor: "#1ab394",
            });

            setDocsChangedFlag(!docsChangedFlag);
          } catch (error) {
            Swal.showValidationMessage(`Error al eliminar: ${error.message}`);
          }
        }
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handleSelectFolder = (folderKey, files, checked) => {
    setSelectedDocs((prev) => {
      const updated = { ...prev };
      if (checked) {
        // Selecciona todos los docs de la carpeta
        updated[folderKey] = files.map((f) => ({
          doc_id: f.doc_id,
          path: f.path,
        }));
      } else {
        // Desmarca todos los docs de la carpeta
        updated[folderKey] = [];
      }
      return updated;
    });
  };

  const handleSelectDoc = (folderKey, file, checked) => {
    setSelectedDocs((prev) => {
      const updated = { ...prev };
      const docs = updated[folderKey] ? [...updated[folderKey]] : [];

      if (checked) {
        // Agregar si no existe
        if (
          !docs.some((d) => d.doc_id === file.doc_id && d.path === file.path)
        ) {
          docs.push({ doc_id: file.doc_id, path: file.path });
        }
      } else {
        // Remover si existe
        updated[folderKey] = docs.filter(
          (d) => !(d.doc_id === file.doc_id && d.path === file.path),
        );
        return updated;
      }

      updated[folderKey] = docs;
      return updated;
    });
  };

  // useEffect(() => {
  //   console.log(docsInfo);
  // }, [docsInfo]);

  // useEffect(() => {
  //   console.log(docsInfoTransfered);
  // }, [docsInfoTransfered]);

  // console.log(dataCase);

  const renderDocumentSection = (
    docsData,
    sectionTitle,
    transfered = false,
  ) => {
    if (!docsData) return <Skeleton width={"100%"} variant="text" />;
    // Si no hay datos, mostrar alerta
    if (
      Object.entries(docsData).length === 0 &&
      !docFlagTransfered &&
      !transfered
    )
      return (
        <Alert severity="info" sx={{ marginBottom: "10px" }}>
          No hay documentación para mostrar
        </Alert>
      );

    // Paso 1: Agrupar por empresa -> caseId -> folderName -> files
    const groupedByCompany = {};

    Object.entries(docsData).forEach(([caseId, folders]) => {
      Object.entries(folders).forEach(([folderName, files]) => {
        files.forEach((file) => {
          // console.log(file);

          const companyName =
            file?.company_relation?.company_name ||
            file?.unmatched_doc?.case_relation?.company_relation
              ?.company_name ||
            file?.matched_doc?.case_relation?.company_relation?.company_name ||
            "Sin nombre asignado";

          if (!groupedByCompany[companyName])
            groupedByCompany[companyName] = {};
          if (!groupedByCompany[companyName][caseId])
            groupedByCompany[companyName][caseId] = {};
          if (!groupedByCompany[companyName][caseId][folderName])
            groupedByCompany[companyName][caseId][folderName] = [];

          groupedByCompany[companyName][caseId][folderName].push(file);
        });
      });
    });

    // Paso 2: Renderizar la estructura manteniendo funcionalidades
    return (
      <div style={{ width: "100%" }}>
        {((sectionTitle && !transfered) ||
          Object.entries(docsData).length !== 0) && <h4>{sectionTitle}</h4>}

        {Object.entries(groupedByCompany).map(([companyName, cases]) => (
          <div key={companyName} style={{ marginBottom: "20px" }}>
            <h4 style={{ color: "#1a73e8" }}>{companyName}</h4>

            {Object.entries(cases).map(([caseId, folders]) => (
              <div key={caseId} style={{ marginLeft: "20px" }}>
                <h4>Expediente #{caseId}</h4>

                {Object.entries(folders).map(([folderName, files]) => {
                  // IMPORTANT: para no romper selectedDocs existentes
                  // mantengo el mismo folderKey que usabas antes: `${caseId}-${folderName}`
                  const folderKey = `${caseId}-${folderName}`;
                  const isOpen = openFolders[folderKey] ?? false;

                  return (
                    <Grid container key={folderKey} direction="column">
                      <Grid container>
                        {/* Checkbox del encabezado de carpeta (mantengo tu lógica) */}
                        {!transfered && (
                          <Checkbox
                            checked={
                              (selectedDocs[folderKey]?.length || 0) ===
                              files.length
                            }
                            indeterminate={
                              selectedDocs[folderKey]?.length > 0 &&
                              selectedDocs[folderKey]?.length < files.length
                            }
                            onChange={(e) =>
                              handleSelectFolder(
                                folderKey,
                                files,
                                e.target.checked,
                              )
                            }
                            onClick={() => toggleFolder(folderKey)}
                          />
                        )}

                        <Grid
                          item
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            cursor: "pointer",
                          }}
                          onClick={() => toggleFolder(folderKey)}>
                          {isOpen ? (
                            <FolderOpen
                              color="warning"
                              sx={{ marginRight: "10px" }}
                            />
                          ) : (
                            <Folder
                              color="warning"
                              sx={{ marginRight: "10px" }}
                            />
                          )}
                          {folderName}
                        </Grid>

                        {/* Contenido de carpeta con animación */}
                        <Collapse
                          in={isOpen}
                          timeout="auto"
                          unmountOnExit
                          sx={{ width: "100%" }}>
                          {files.map((file, index) => {
                            const fileInputId = `${folderKey}-file-${index}`;
                            return (
                              <Grid
                                key={fileInputId}
                                item
                                xs={12}
                                container
                                sx={{
                                  borderBottom: "1px solid #80808057",
                                  marginBottom: "10px",
                                  paddingLeft: "20px",
                                }}>
                                {/* Columna principal con checkbox y enlace */}
                                <Grid
                                  item
                                  xs={11}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    marginBottom: "5px",
                                  }}>
                                  {/* Checkbox por archivo (restaurado) */}
                                  {/* (file?.unmatched_doc?.case_relation
                                    ?.company_relation?.id ===
                                    dataCase?.company_relation?.id ||
                                    file?.matched_doc?.case_relation
                                      ?.company_relation?.id ===
                                      dataCase?.company_relation?.id) ejemplo de relaciones */}
                                  {!transfered && (
                                    <Checkbox
                                      checked={
                                        selectedDocs[folderKey]?.some(
                                          (doc) =>
                                            doc.doc_id === file.doc_id &&
                                            doc.path === file.path,
                                        ) || false
                                      }
                                      onChange={(e) =>
                                        handleSelectDoc(
                                          folderKey,
                                          file,
                                          e.target.checked,
                                        )
                                      }
                                    />
                                  )}

                                  <InsertDriveFile
                                    color="info"
                                    sx={{ marginRight: "10px" }}
                                  />
                                  <a
                                    href={`${
                                      process.env.REACT_APP_S3_API_URL
                                    }/${encodeS3Path(file.path)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      wordBreak: "break-word",
                                      whiteSpace: "normal",
                                      display: "block",
                                      width: "100%",
                                      textDecoration: "none",
                                      ...(file.matched
                                        ? { fontWeight: "700" }
                                        : {}),
                                    }}
                                    id={fileInputId + "-ancle"}>
                                    {file?.matched
                                      ? file?.doc_title
                                      : file?.file_name}
                                  </a>

                                  {/* Icono de descripción si existe (restaurado) */}
                                  {file?.doc_desc ? (
                                    <CustomIconsPlusText
                                      icon={Info}
                                      severity={
                                        file?.doc_desc?.includes("Recibido")
                                          ? "info"
                                          : "warning"
                                      }
                                      styles={{ width: "15px", height: "15px" }}
                                      text={`${file?.doc_desc}`}
                                    />
                                  ) : null}
                                </Grid>

                                {/* Botón para eliminar archivo (restaurado) */}

                                {(file?.unmatched_doc?.case_relation
                                  ?.company_relation?.id ===
                                  dataCase?.company_relation?.id ||
                                  file?.matched_doc?.case_relation
                                    ?.company_relation?.id ===
                                    dataCase?.company_relation?.id) &&
                                  can("cases", "deletedocs") && (
                                    <Grid
                                      item
                                      xs={1}
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        padding: "0px 5px 5px 5px",
                                      }}>
                                      <IconButton
                                        color="error"
                                        aria-label="cerrar"
                                        onClick={() => handleDelete(file)}
                                        sx={{
                                          border: "1px solid",
                                          borderColor: "error.main",
                                          borderRadius: "4px",
                                          margin: "auto",
                                        }}>
                                        <Delete color="error" />
                                      </IconButton>
                                    </Grid>
                                  )}

                                {/* Campos adicionales para nombrar archivo (restaurado) */}
                                {!file?.matched && can("cases", "docs") && (
                                  <Grid
                                    item
                                    xs={12}
                                    sx={{ marginBottom: "5px" }}>
                                    <TextField
                                      size="small"
                                      label={"Nombrar archivo"}
                                      id={fileInputId}
                                    />
                                    <Button
                                      variant="outlined"
                                      color="primary"
                                      style={{ height: "100%" }}
                                      onClick={() => {
                                        handleNewDocumentName(
                                          fileInputId,
                                          file?.file_name,
                                          file?.path,
                                        );
                                      }}>
                                      <CloudUpload color="primary" />
                                    </Button>
                                  </Grid>
                                )}

                                {/* Mensaje de estado del archivo (restaurado) */}
                                {file.matched ? (
                                  <Grid
                                    item
                                    xs={12}
                                    sx={{
                                      paddingLeft: "5px",
                                      fontStyle: "italic",
                                      color: "gray",
                                      marginBottom: "5px",
                                    }}>
                                    <div>Subido por: {file?.lawyer_name}</div>
                                    <>Fecha: {file?.date_in}</>
                                  </Grid>
                                ) : (
                                  <Grid
                                    item
                                    xs={12}
                                    sx={{
                                      paddingLeft: "5px",
                                      fontStyle: "italic",
                                      color: "gray",
                                      marginBottom: "5px",
                                    }}>
                                    El archivo aún no ha sido nombrado
                                  </Grid>
                                )}
                              </Grid>
                            );
                          })}
                        </Collapse>
                      </Grid>
                    </Grid>
                  );
                })}
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  return (
    <Box sx={{ paddingLeft: "10px", width: "100%" }}>
      <Alert severity="info" sx={{ mb: "10px" }}>
        Solo tiene permitido eliminar información del caso actual.
      </Alert>
      {renderDocumentSection(
        docsInfo,
        `Documentación del caso en ${dataCase?.company_name}`,
      )}
      {renderDocumentSection(
        docsInfoTransfered,
        "Documentos transferidos",
        true,
      )}
    </Box>
  );
};

export default ArchivosMostrar;
